/**
 * `PatientInfo` -- the FIRST real, in-folder `componentId` slot component (CDMX-18402), registered
 * into `registry.tsx`'s `DEFAULT_SLOT_REGISTRY` under the id `'PatientInfo'` and wired into both
 * `DEFAULT_CODING_SCREEN_CONFIG`/`DEFAULT_GRADING_SCREEN_CONFIG`'s `header.patientSummary.fields`
 * (`config/defaultConfig.ts`) at `contentId: 'case.patient'` (coding) / `'workitem.patient'`
 * (grading). Rendered by `PatientSummaryBar`'s `<Tag><Slot .../></Tag>` host exactly like every
 * other patient-summary field -- `Slot` resolves `componentId: 'PatientInfo'` against the injected
 * registry and forwards `item`/`value`/`mode` verbatim (`molecules/Slot/Slot.tsx`).
 *
 * BEHAVIOR -- VISIBLE TEXT + STYLING UPDATED by the CDMX-18402
 * restyle pass to match the live-inspected design; the data contract, every `data-testid`, and the
 * value-guard behavior below are UNCHANGED:
 *  - renders `Gender, Age yrs` inline (e.g. `"M, 48 yrs"`) -- a missing/malformed age renders
 *    the bare `"N/A"` placeholder, never `"N/A yrs"` (see `agePart` below).
 *  - hovering the inline text reveals a popover listing Name / Date of birth / MRN /
 *    Account # / Visit # / Case ID.
 *  - every displayed value is exactly what the bound object carries, with ONE deliberate exception:
 *    Date of birth is reformatted from its stored ISO `YYYY-MM-DD` shape to a human `MMM D, YYYY`
 *    (e.g. `1945-08-03` -> `"Aug 3, 1945"`) via `formatDob` below. Every other field is untransformed
 *    beyond the `"N/A"` fallback; a dob that is absent, non-string, or not ISO-shaped is NOT crashed
 *    on -- it falls back to `"N/A"` (absent/non-string) or renders verbatim (a present-but-unparseable
 *    string), so the "never throws / always renders" guarantee below still holds for it.
 *  - the popover closes when the cursor leaves -- Chakra's `Popover trigger="hover"` handles
 *    open/close itself (mouse AND keyboard-focus, per the ARIA disclosure pattern); this component
 *    adds NO manual open/close state.
 *  - a missing field (or a missing/non-object `value` entirely) renders `"N/A"` for that field
 *    without ever throwing -- the popover still renders. This is the lazy-fallback fix
 *    (`molecules/Slot/Slot.tsx`'s `fallback` thunk) made concrete: `Slot` only calls its `fallback`
 *    when `componentId` is unset, so a `componentId: 'PatientInfo'` slot bound to a structured
 *    (non-primitive) `value` reaches THIS component, never `renderPlainText` (which throws on
 *    non-primitives by design, `slots/renderPlainText.ts`) -- `PatientInfo` is exactly the
 *    real-component case that whole mechanism exists for.
 *
 * VALUE GUARD (hardened): `value` (`SlotProps.value`) is
 * `unknown` -- read via `readState()`, so it may be `undefined` (no `state`/no matching `contentId`
 * key -- the common case until CDMX-18295 wires a real adapter), `null`, an array, or (a config/data
 * bug) a non-object primitive. `data` narrows defensively: anything that isn't a genuine, non-null,
 * non-array object collapses to `{}`, so every field reads as `undefined` and falls back to `"N/A"`.
 * A SECOND, independent guard (`na()` below) protects every individual field even once `data` itself
 * passes that check: a field that is PRESENT but the wrong shape (e.g. `{ age: {} }`) still renders
 * `"N/A"` instead of reaching JSX as a non-primitive child, which React throws on
 * ("Objects are not valid as a React child") -- never a runtime crash either way.
 *
 * RENDER-BEHAVIOR NOTE: `Popover` is used WITHOUT `isLazy`, so `PopoverContent`'s rows are
 * always mounted in the DOM (`@chakra-ui/lazy-utils`'s `lazyDisclosure` short-circuits to `true`
 * whenever `isLazy` is unset) -- the popover's rows ("the popover still renders") hold structurally, not just
 * while hovered; only VISIBILITY (a plain CSS toggle driven by Chakra's own `isOpen` state, no state
 * of ours) depends on hover/focus. See `PatientInfo.test.tsx`'s own render-behavior note for how
 * this shapes the test assertions.
 *
 * STYLING (CDMX-18402 restyle pass -- literal values from the live-inspected design, deliberately
 * NOT `styles/tokens.ts` constants: none of that file's existing color/geometry tokens are both a
 * close-enough match AND semantically scoped to a floating popover card -- they target the shell's
 * page/strip/card chrome -- so this file stays on plain literals throughout rather than force a
 * near-miss token onto an unrelated surface). `PopoverHeader`/`PopoverBody`/`PopoverArrow` are
 * deliberately NOT used: a plain `Box` stands in for the header (Chakra's `PopoverHeader` always
 * carries a `borderBottomWidth`, which this design has none of), and `PopoverContent` hosts the
 * header/rows/divider directly as plain children -- its own theme `baseStyle` sets no
 * `display:flex` (verified in `@chakra-ui/theme`'s `popover.ts`), so those block-level children
 * already stack vertically with no extra flex wiring needed; each row is its OWN internal `Flex`
 * (label left / value right). Every `PopoverContent` style prop below (`bg`/`border`/`borderRadius`/
 * `boxShadow`/`width`) is set explicitly, overriding Chakra's popover-theme defaults for those same
 * properties.
 *
 * Isolation: imports ONLY `react`, `@chakra-ui/react`, and the two type-only in-folder deep paths
 * (`../../interface/patientInfo`, `../../interface/slotProps`) -- no other project_starburst import
 * (in particular, NOT `../../registry`, so registering this component into `registry.tsx` creates no
 * import cycle) -- satisfying the boundary guard (`../../__tests__/boundary.test.ts`).
 */
import {
  Box,
  Flex,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { PatientInfoData } from '../../interface/patientInfo';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  INK_SOFT,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW,
  SURFACE_WARM
} from '../../styles/tokens';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

const na = (v: unknown): string => typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

const MONTHS_ABBR = [ 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec' ] as const;

/**
 * Reformats a stored date of birth from ISO `YYYY-MM-DD` (optionally with a trailing time component,
 * which is ignored) into the human `MMM D, YYYY` display form, e.g. `'1945-08-03'` -> `'Aug 3, 1945'`
 * (no leading zero on the day). Deliberately parses the string's own numeric parts rather than
 * `new Date(iso)`: `new Date('1945-08-03')` is UTC midnight, so `toLocaleDateString` in any timezone
 * behind UTC would render the PREVIOUS calendar day -- a date of birth must never drift by a day.
 *
 * Stays inside `PatientInfo`'s isolation boundary (no `date-fns`/`dayjs` import -- neither is on the
 * `project_starburst` allowlist). Fails soft in the same spirit as `na()`: a non-string (absent /
 * malformed shape) returns `undefined` so the row falls back to `"N/A"`; a present string that is not
 * ISO-shaped or carries an out-of-range month/day is returned VERBATIM rather than dropped, so a real
 * (if unexpected) stored value is never silently lost.
 */
const formatDob = (raw: unknown): string | undefined => {
  if (typeof raw !== 'string') return undefined;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw.trim());
  if (!match) return raw;
  const [, year, month, day] = match;
  const monthIndex = Number(month) - 1;
  const dayNumber = Number(day);
  if (monthIndex < 0 || monthIndex > 11 || dayNumber < 1 || dayNumber > 31) {
    return raw;
  }
  return `${MONTHS_ABBR[monthIndex]} ${dayNumber}, ${year}`;
};

/** One popover-body row: a stable kebab-case id (stamped as `data-testid="patient-info-<id>"`) +
 *  its label + the raw (possibly absent) field value -- `na()` is applied once, at render, not
 *  baked into this table. */
interface PatientInfoRow {
  readonly id: string;
  readonly label: string;
  readonly value?: string;
}

/**
 * Renders one label/value popover row: a flex row, label left / value right,
 * `justifyContent="space-between"`, per the design. Label and value share the app-default font;
 * the value is set apart by its role weight (`Caption 1` bold vs the label's `Caption 2`) and
 * color (`INK`).
 */
const PatientInfoRowView: React.FC<{
  readonly row: PatientInfoRow;
}> = ({ row }) => (
  <Flex
    justifyContent="space-between"
    gap="20px"
    lineHeight="1.6"
    data-testid={`patient-info-${row.id}`}
  >
    <Text as="span" textStyle="Caption 2" color={INK_SOFT}>
      {row.label}
    </Text>
    <Text as="span" textStyle="Caption 1" color={INK}>
      {na(row.value)}
    </Text>
  </Flex>
);

export const PatientInfo: React.FC<SlotProps> = ({ value }) => {
  // Verbatim value guard (hardened) -- `value`
  // is `unknown`; only a genuine, non-null, non-array object is trusted as `PatientInfoData`,
  // everything else (undefined, null, a string/number/boolean/array/...) collapses to `{}` so every
  // field below reads `undefined` and renders "N/A", never throws. Arrays are excluded
  // explicitly even though `typeof [] === 'object'` -- an array is not a genuine record, and relying
  // on it merely reading `undefined` for every named field would be an accident of shape, not an
  // intentional guarantee.
  const data: PatientInfoData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as PatientInfoData)
      : {};

  // Inline-trigger format (CDMX-18402 restyle): `{Gender}, {Age} yrs` -- gender FIRST (via the
  // shared `na()` guard above), then age with a " yrs" suffix ONLY when it is genuinely present as
  // a string/number; a missing/malformed age collapses to the SAME bare "N/A" placeholder every
  // other field uses, never "N/A yrs". This is a small, deliberately separate ternary from `na()`
  // (not a reuse of it) because `na()` alone has no way to know whether to append the suffix.
  const genderPart = na(data.gender);
  const agePart =
    typeof data.age === 'string' || typeof data.age === 'number'
      ? `${data.age} yrs`
      : 'N/A';

  const nameRows: readonly PatientInfoRow[] = [
    { id: 'full-name', label: 'Name', value: data.fullName },
    { id: 'dob', label: 'Date of birth', value: formatDob(data.dob) }
  ];
  const identifierRows: readonly PatientInfoRow[] = [
    { id: 'mrn', label: 'MRN', value: data.mrn },
    { id: 'account-number', label: 'Account #', value: data.accountNumber },
    { id: 'visit-number', label: 'Visit #', value: data.visitNumber },
    { id: 'case-id', label: 'Case ID', value: data.caseId }
  ];

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Text
          as="span"
          tabIndex={0}
          aria-label={`Patient information: age ${na(data.age)}, gender ${na(data.gender)}`}
          data-testid="patient-info"
          textStyle="Body 2"
          color={INK}
          sx={HOVER_HINT_SX}
        >
          {genderPart}, {agePart}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="patient-info-popover"
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="11px 13px"
          width="226px"
        >
          <Box
            textStyle="Overline 1"
            color={INK_MUTED}
            textTransform="uppercase"
            marginBottom="8px"
          >
            Patient
          </Box>
          {nameRows.map((row) => (
            <PatientInfoRowView key={row.id} row={row} />
          ))}
          <Box
            borderTop={`1px solid ${POPOVER_BORDER}`}
            marginTop="2px"
            paddingTop="8px"
          >
            {identifierRows.map((row) => (
              <PatientInfoRowView key={row.id} row={row} />
            ))}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
